import React, { useEffect, useMemo, useState } from 'react';
import { Minus, Plus, Tag, Loader2, X, BadgeCheck, AlertCircle } from 'lucide-react';
import { checkPromo, getMemberBusinesses } from '../lib/api';
import { money } from '../lib/events';

const labelCls = 'font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase';
const inputCls = 'w-full mt-1.5 px-4 py-3.5 bg-white border border-neutral-200 rounded-sm font-sans-ah text-[15px] focus:outline-none focus:border-[#4a9cd4] transition-colors';
const stepBtn = 'w-9 h-9 rounded-full border border-[#4a9cd4] text-[#4a9cd4] flex items-center justify-center hover:bg-[#4a9cd4] hover:text-white transition-colors disabled:opacity-30 disabled:pointer-events-none';

export const payOptions = (ev) => {
  const p = ev.payment || { pay_at_door: true };
  return [
    p.pay_at_door !== false && ['door', 'Pay at the door'],
    p.etransfer && ['etransfer', `E-transfer${p.etransfer_email ? ` to ${p.etransfer_email}` : ''}`],
    p.online && p.online_link && ['online', 'Pay online now'],
  ].filter(Boolean);
};

export const computeTotals = (ev, v) => {
  const lines = (ev.ticket_types || []).map((t) => ({ ...t, qty: v.items[t.id] || 0 })).filter((t) => t.qty > 0);
  const qty = lines.reduce((a, t) => a + t.qty, 0);
  const subtotal = lines.reduce((a, t) => a + t.qty * (t.price || 0), 0);
  const p = v.promo;
  const discount = p ? Math.min(subtotal, p.kind === 'percent' ? (subtotal * Math.min(p.value, 100)) / 100 : p.value) : 0;
  return { lines, qty, subtotal, discount, total: Math.max(0, subtotal - discount), needsBusiness: lines.some((t) => t.members_only) };
};

function TypeRow({ t, qty, onQty, maxReached }) {
  return (
    <div className="flex items-center gap-4 py-3.5 border-b border-neutral-100 last:border-0" data-testid={`ticket-type-${t.id}`}>
      <div className="flex-1 min-w-0">
        <p className="font-sans-ah text-[15px] text-[#2f2f2f] flex items-center gap-2">{t.name}
          {t.members_only && <span className="text-[10px] tracking-wide-ah uppercase bg-[#eef4f8] text-[#4a9cd4] px-2 py-0.5 rounded-full">Members</span>}
        </p>
        {t.description && <p className="font-sans-ah text-[12px] text-[#999]">{t.description}</p>}
      </div>
      <span className="font-serif-ah text-[20px] text-[#2f2f2f] w-20 text-right">{money(t.price)}</span>
      <div className="flex items-center gap-2">
        <button type="button" aria-label={`Fewer ${t.name}`} data-testid={`ticket-minus-${t.id}`} className={stepBtn} disabled={qty <= 0} onClick={() => onQty(qty - 1)}><Minus size={15} /></button>
        <span className="w-6 text-center font-sans-ah text-[16px]" data-testid={`ticket-qty-${t.id}`}>{qty}</span>
        <button type="button" aria-label={`More ${t.name}`} data-testid={`ticket-plus-${t.id}`} className={stepBtn} disabled={maxReached} onClick={() => onQty(qty + 1)}><Plus size={15} /></button>
      </div>
    </div>
  );
}

const normBiz = (v) => (v || '').toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, ' ').split(' ')
  .filter((w) => w && !['the', 'inc', 'ltd', 'limited', 'corp', 'co'].includes(w)).join(' ');

function MemberBusinessField({ value, onChange }) {
  const [names, setNames] = useState([]);
  useEffect(() => { getMemberBusinesses().then(setNames).catch(() => {}); }, []);
  const index = useMemo(() => new Map(names.map((n) => [normBiz(n), n])), [names]);
  const found = index.get(normBiz(value));
  return (
    <div>
      <label htmlFor="rsvp-business" className={labelCls}>Member Business Name *</label>
      <input id="rsvp-business" list="member-business-list" autoComplete="off" data-testid="rsvp-business-input" className={inputCls}
        value={value} onChange={(e) => onChange(e.target.value)} placeholder="Start typing your Chamber member business" />
      <datalist id="member-business-list">{names.map((n) => <option key={n} value={n} />)}</datalist>
      {value.trim() && names.length > 0 && (found ? (
        <p className="font-sans-ah text-[13px] text-green-700 mt-1.5 flex items-center gap-1.5" data-testid="member-verified"><BadgeCheck size={15} /> Verified Chamber member: {found}</p>
      ) : (
        <p className="font-sans-ah text-[13px] text-amber-700 mt-1.5 flex items-center gap-1.5" data-testid="member-not-found"><AlertCircle size={15} /> Not found in our member directory. Pick your business from the list, or choose a non-member ticket.</p>
      ))}
    </div>
  );
}

function PromoField({ event, value, onChange }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (!event.has_promo_codes) return null;
  if (value.promo) {
    return (
      <div className="flex items-center gap-2 font-sans-ah text-[14px] text-green-700" data-testid="promo-applied">
        <BadgeCheck size={17} /> Code <strong>{value.promo.code}</strong> applied ({value.promo.kind === 'percent' ? `${value.promo.value}% off` : `${money(value.promo.value)} off`})
        <button type="button" onClick={() => onChange({ ...value, promo: null })} aria-label="Remove promo code" data-testid="promo-remove-btn" className="p-1 text-[#999] hover:text-red-500"><X size={14} /></button>
      </div>
    );
  }
  const apply = async () => {
    if (!code.trim()) return;
    setBusy(true); setErr('');
    try { onChange({ ...value, promo: await checkPromo(event.id, code.trim()) }); setCode(''); } catch (e) { setErr(e?.response?.data?.detail || 'Invalid code'); }
    setBusy(false);
  };
  return (
    <div>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Tag size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#aaa]" />
          <input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); apply(); } }}
            placeholder="Promo code" aria-label="Promo code" data-testid="promo-code-input"
            className="w-full pl-10 pr-3 py-3 bg-white border border-neutral-200 rounded-sm font-sans-ah text-[14px] uppercase focus:outline-none focus:border-[#4a9cd4]" />
        </div>
        <button type="button" onClick={apply} disabled={busy || !code.trim()} data-testid="promo-apply-btn"
          className="font-sans-ah text-[12px] tracking-wide-ah px-5 rounded-sm border border-[#4a9cd4] text-[#4a9cd4] hover:bg-[#4a9cd4] hover:text-white transition-colors disabled:opacity-40 flex items-center gap-2">
          {busy && <Loader2 size={14} className="animate-spin" />} APPLY
        </button>
      </div>
      {err && <p className="font-sans-ah text-[13px] text-red-500 mt-1.5" data-testid="promo-error">{err}</p>}
    </div>
  );
}

export function TicketPicker({ event, value, onChange, guestsSlot = null }) {
  const t = computeTotals(event, value);
  const opts = payOptions(event);
  const setQty = (id, qty) => onChange({ ...value, items: { ...value.items, [id]: Math.max(0, qty) } });
  const only = opts.length === 1 ? opts[0][0] : '';
  useEffect(() => { if (only && value.payment_method !== only) onChange({ ...value, payment_method: only }); }, [only, value, onChange]);
  return (
    <div className="space-y-5" data-testid="ticket-picker">
      <div>
        <p className={labelCls}>Tickets *</p>
        <div className="mt-1">{event.ticket_types.map((tt) => <TypeRow key={tt.id} t={tt} qty={value.items[tt.id] || 0} onQty={(q) => setQty(tt.id, q)} maxReached={t.qty >= 20} />)}</div>
      </div>
      {guestsSlot}
      {t.needsBusiness && (
        <MemberBusinessField value={value.business_name} onChange={(business_name) => onChange({ ...value, business_name })} />
      )}
      <PromoField event={event} value={value} onChange={onChange} />
      {t.qty > 0 && (
        <div className="bg-[#f8f6f2] rounded-sm p-4 font-sans-ah text-[14px] space-y-1" data-testid="rsvp-order-summary">
          {t.lines.map((l) => <div key={l.id} className="flex justify-between text-[#555]"><span>{l.qty} × {l.name}</span><span>{money(l.qty * (l.price || 0))}</span></div>)}
          {t.discount > 0 && <div className="flex justify-between text-green-700"><span>Discount ({value.promo.code})</span><span>−{money(t.discount)}</span></div>}
          <div className="flex justify-between pt-2 mt-1 border-t border-neutral-200 text-[#2f2f2f] font-medium text-[16px]"><span>Total</span><span data-testid="rsvp-total">{money(t.total)}</span></div>
        </div>
      )}
      {t.total > 0 && opts.length > 0 && (
        <fieldset>
          <legend className={labelCls}>How will you pay? *</legend>
          <div className="mt-2 space-y-2">
            {opts.map(([k, label]) => (
              <label key={k} className={`flex items-center gap-3 px-4 py-3 rounded-sm border cursor-pointer transition-colors ${value.payment_method === k ? 'border-[#4a9cd4] bg-[#f4f9fc]' : 'border-neutral-200 hover:border-[#4a9cd4]'}`}>
                <input type="radio" name="pay" value={k} checked={value.payment_method === k} onChange={() => onChange({ ...value, payment_method: k })} data-testid={`pay-method-${k}`} />
                <span className="font-sans-ah text-[14px] text-[#2f2f2f]">{label}</span>
              </label>
            ))}
          </div>
          {event.payment?.instructions && <p className="font-sans-ah text-[12px] text-[#888] mt-2">{event.payment.instructions}</p>}
        </fieldset>
      )}
    </div>
  );
}
